import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type {
  Budget, BudgetLine, BudgetReport, CopyBudgetInput, EstimateInput, HarvestProgress, Insights,
} from '../types/insights';

// ============================================================================
// INSIGHTS SERVICE — Phase 4 of the harvest & labour plan, /api/field-work.
// Reads need a farm_jobs supervisor, creator or admin seat (money stripped by
// the server for non-admins); crop estimates need creator/admin; budgets admin.
// ============================================================================

export interface SeasonFilters {
  season?: number;
  farm_id?: number;
  activity_type_id?: number;
}

function qs(params: SeasonFilters): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null) p.append(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

class InsightsService {
  async getInsights(filters: SeasonFilters): Promise<Insights> {
    return unwrap(await apiService.get<Insights>(`/field-work/insights${qs(filters)}`));
  }

  async getBudget(season: number): Promise<Budget> {
    return unwrap(await apiService.get<Budget>(`/field-work/budgets/${season}`));
  }

  async saveBudget(season: number, lines: BudgetLine[], notes?: string | null): Promise<Budget> {
    const body = {
      notes,
      lines: lines.map(l => ({
        farm_id: l.farm_id, block_id: l.block_id, cultivar_area_id: l.cultivar_area_id, activity_type_id: l.activity_type_id,
        hours: l.hours, labour_cost: l.labour_cost ?? null, bins: l.bins, tonnes: l.tonnes, notes: l.notes ?? null,
      })),
    };
    return unwrap(await apiService.put<Budget>(`/field-work/budgets/${season}`, body));
  }

  async copyBudget(season: number, input: CopyBudgetInput): Promise<Budget> {
    return unwrap(await apiService.post<Budget>(`/field-work/budgets/${season}/copy`, input));
  }

  async budgetReport(filters: SeasonFilters): Promise<BudgetReport> {
    return unwrap(await apiService.get<BudgetReport>(`/field-work/reports/budget${qs(filters)}`));
  }

  async harvestProgress(filters: SeasonFilters): Promise<HarvestProgress> {
    return unwrap(await apiService.get<HarvestProgress>(`/field-work/crop-estimates${qs(filters)}`));
  }

  async saveEstimates(season: number, items: EstimateInput[]): Promise<HarvestProgress> {
    return unwrap(await apiService.put<HarvestProgress>(`/field-work/crop-estimates/${season}`, { items }));
  }
}

export default new InsightsService();
